/* eslint-disable max-len */
import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faCalendarAlt,
  faCopy,
  faExclamationCircle,
  faEye,
  faEyeSlash,
  faLock,
  faPencil,
  faTrashAlt,
  faUnlock,
} from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faPlusCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, Form, Grid, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import useCampaignQuote, { useRelations } from '@hooks/useCampaignQuote'
import useCampaignQuoteItem, { useCampaignQuoteItemForm } from '@hooks/useCampaignQuoteItem'
import useSelector from '@hooks/useSelector'

import { checkEditionValid } from '@functions/publicationEdition'
import { confirmDelete } from '@functions/entity'
import { paidByOptions } from '@functions/campaignQuoteItem'

import CampaignQuoteItemToolTip from '@components/CampaignQuoteItemToolTip'
import CustomItemSearch from '@components/CustomItemSearch'
import ProviderSubHeader from '@components/ProviderSubHeader'

import type { PublicationEditionModel } from '@models/publicationEdition'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { CampaignQuoteModel } from '@models/types'

import ArtworkButton from './components/ArtworkButton'

const MODAL_KEY = 'CreateOrEditCampaignQuoteItemModal'

type ChangePublicationWeek = {
  value: number,
  setEntityState: ReturnType<typeof useCampaignQuoteItemForm>['setEntityState'],
}

const changePublicationWeek = (params: ChangePublicationWeek) => {
  const { value, setEntityState } = params

  setEntityState({
    publication_week: value,
    row_title: `Week ${value}`,
  })
}

type RenderItemDateParams = {
  campaignQuoteItemFormPayload: ReturnType<typeof useCampaignQuoteItemForm>,
  editPublicationEdition: ReturnType<typeof useCampaignQuoteItem>['callbacks']['editPublicationEdition'],
  publicationEdition: PublicationEditionModel,
  publicationEditionValid: boolean,
  recalculateEditions: boolean,
}

const renderItemDate = (params: RenderItemDateParams) => {
  const {
    campaignQuoteItemFormPayload: {
      entityState: {
        publication_edition_id,
        publication_week,
        required_by,
      },
      errors,
      handlers,
      setEntityState,
    },
    editPublicationEdition,
    publicationEdition,
    publicationEditionValid,
    recalculateEditions,
  } = params

  // If recalculateEditions is enabled don't show dates as they are irrelevant
  if (publication_edition_id && recalculateEditions){
    return (
      <Form.Field direction="column" label="Publication Week #">
        <input
          min="1"
          name="publication_week"
          onChange={e => changePublicationWeek({
            setEntityState,
            value: Number(e.target.value),
          })}
          type="number"
          value={publication_week}
        />
      </Form.Field>
    )
  }

  // Item is assigned to an Edition
  if (publication_edition_id){
    return (
      <Form.Field direction="column" label="Publication Edition">
        <Box
          alignItems="center"
          border="1px solid"
          borderColor="lineColor"
          borderRadius={5}
          color={publicationEditionValid ? 'bodyFontColor' : 'red'}
          fontSize="small"
          height={37}
          padding="medium"
          onClick={() => editPublicationEdition({
            callbacks: {
              selectPublicationEditionId: (payload: HandleCallbackActionParams) => {
                const { callbacks: { afterAction }, entityParams } = payload

                // @ts-expect-error TS18048
                setEntityState({ publication_edition_id: entityParams.publication_edition_id })
                // @ts-expect-error
                if (afterAction) afterAction()
              },
            },
            payload: {
              currentEditionId: publication_edition_id,
            },
          })}
        >
          <FontAwesomeIcon
            icon={!publicationEditionValid ? faExclamationCircle : faCalendarAlt}
            style={{ marginRight: 5 }}
          />

          {!publicationEditionValid ? 'Edition Passed' : publicationEdition.publication_date}
        </Box>
      </Form.Field>
    )
  }

  return (
    <Form.Field label="Required By" errorMessage={errors.required_by}>
      <input name="required_by" type="date" value={required_by} {...handlers} />
    </Form.Field>
  )
}

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuoteItem: (payload: HandleCallbackActionParams) => void,
  duplicateCampaignQuoteItem: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuoteItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

type ArrayElement<T> = T extends (infer U)[] ? U : never;

type RenderActualPriceTextParams = {
  invalidPrice: ArrayElement<CampaignQuoteModel['invalidItems']> | undefined,
}

const renderActualPriceText = (params: RenderActualPriceTextParams) => {
  const { invalidPrice } = params

  return (

    <Box alignItems="center" justifyContent="space-between">
      <span>Actual Price</span>

      {invalidPrice && (
        <CampaignQuoteItemToolTip
          boxProps={{ position: 'absolute', left: 0 }}
          invalidPrice={invalidPrice}
          fontSize="xxsmall"
          solidIcon
          iconColor="lightGrey"
        />
      )}
    </Box>
  )
}

const CreateOrEditCampaignQuoteItemModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const {
    closeModal,
    createCampaignQuoteItem,
    duplicateCampaignQuoteItem,
    updateCampaignQuoteItem,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}

  const initCampaignQuoteItem = modalPayload?.campaignQuoteItem || {}

  const campaignQuoteItemPayload = useCampaignQuoteItem(initCampaignQuoteItem)
  const {
    callbacks: {
      createCampaignQuoteItem: createFn,
      deleteCampaignQuoteItem: deleteFn,
      duplicateCampaignQuoteItem: duplicateFn,
      editQuantityAndOptions,
      editPublicationEdition,
      updateCampaignQuoteItem: updateFn,
    },
    campaignQuote,
    campaignQuoteItem,
    campaignQuoteItem: {
      deep_link,
      deep_link_url,
      package_id,
    },
    creating,
    isQuantityEditable,
    recalculateEditions,
    updating,
  } = campaignQuoteItemPayload

  const campaignQuoteItemFormPayload = useCampaignQuoteItemForm(campaignQuoteItem, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      description,
      display_title,
      hidden,
      internal_notes,
      item_type,
      locked,
      paid_by,
      price_override,
      price,
      price_type,
      provider_id,
      publication_edition_id,
      row_title,
      selected,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = campaignQuoteItemFormPayload

  const newRecord = !campaignQuoteItem.id

  const {
    callbacks: {
      validateCampaignQuotePrices: validateFn,
    },
  } = useCampaignQuote(campaignQuote)

  const { campaignQuoteTemplate } = useRelations(campaignQuote)
  // @ts-expect-error TS2339
  const { disable_price_override } = campaignQuoteTemplate

  // @ts-expect-error TS2339
  const invalidItems = (campaignQuote?.invalidItems ?? []) as CampaignQuoteModel['invalidItems']
  const invalidPrice = invalidItems?.find(item => item?.campaign_quote_item_id === campaignQuoteItem.id)

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)
  const publicationEdition = publicationEditions[publication_edition_id] || {}

  const createOrUpdateCampaignQuoteItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions,
    toastText: `Quote Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Quote Item" />

      {!newRecord && (
        <ProviderSubHeader providerId={provider_id} secondaryText={`Quote Item #${campaignQuoteItem?.id}`} />
      )}

      <SidebarModal.Content hasSubHeader={!newRecord && provider_id}>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.display_title}>
            {newRecord && item_type === 'custom' && (
              <CustomItemSearch
                callbacks={{
                  onChange: value => setEntityState({ display_title: value, title: value }),
                  selectItem: item => setEntityState({
                    custom_item_id: item.id,
                    description: item.description,
                    display_title: item.title,
                    title: item.title,
                    price: item.price,
                    price_override: item.price_override || item.price,
                    price_type: 'fixed',
                  }),
                }}
                value={display_title}
              />
            )}
            {!newRecord && <input name="display_title" type="text" value={display_title || ''} {...handlers} />}
          </Form.Field>

          {!!invalidPrice && (
            <Text color="orange" fontSize="xsmall" marginTop="medium">
              <FontAwesomeIcon icon={faExclamationCircle} /> The price for this item was updated in the price list.
            </Text>
          )}

          <Form.Row marginTop="large">
            {/*
              // @ts-expect-error */}
            <Form.Field label={renderActualPriceText({ invalidPrice })} errorMessage={errors.price}>
              <input disabled name="price" type="text" value={price_type === 'percentage' ? `${price}%` : `$${price}`} {...handlers} />
            </Form.Field>

            {/*
              // @ts-expect-error */}
            <Form.Field label="Override Price" errorMessage={errors.price_override} hideErrorMessage marginTop={['large', 'small']}>
              <input
                disabled={price_type === 'percentage' || disable_price_override}
                name="price_override"
                onBlur={(e) => {
                  handlers.onBlur(e)
                  if (item_type === 'custom' && e.target.value !== '') setEntityState({ price: e.target.value })
                }}
                onChange={(e) => {
                  handlers.onChange(e)
                  if (item_type === 'custom' && e.target.value === '') setEntityState({ price: '0' })
                }}
                type="text"
                value={price_override || ''}
              />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Item Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              placeholder="Visible on Quote"
              value={description || ''}
              style={{ height: 80 }}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Internal Notes" errorMessage={errors.internal_notes} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="internal_notes"
              placeholder="Hidden on Quote"
              value={internal_notes || ''}
              style={{ height: 80 }}
              {...handlers}
            />
          </Form.Field>

          <Form.Row marginTop="large">
            {renderItemDate({
              campaignQuoteItemFormPayload,
              editPublicationEdition,
              publicationEdition,
              publicationEditionValid: checkEditionValid({
                publicationEditionId: publication_edition_id,
                publicationEdition,
              }),
              recalculateEditions,
            })}

            <Form.Field label="Row Title" errorMessage={errors.row_title}>
              <input name="row_title" type="text" value={row_title || ''} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Paid By" errorMessage={errors.paid_by} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="paid_by" type="text" value={paid_by || ''} onChange={handlers.onChange}>
              <option value="">Please Select...</option>
              {paidByOptions.map(paidByOption => (
                <option key={paidByOption.key} value={paidByOption.key}>
                  {paidByOption.text}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="Status" marginTop="large">
            <Box>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={selected ? faCheckCircle : faPlusCircle} />}
                onClick={() => setEntityState({ selected: !selected })}
                size="medium"
                style={{ color: selected ? '#43ac6a' : undefined, marginRight: 8 }}
                width="calc(100% / 2 - 4px)"
              >
                {selected ? 'Selected' : 'Not Selected'}
              </Button>

              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={locked ? faLock : faUnlock} />}
                onClick={() => setEntityState({ locked: !locked, selected: !locked ? true : selected })}
                size="medium"
                width="calc(100% / 2 - 4px)"
              >
                {locked ? 'Required' : 'Optional'}
              </Button>
            </Box>
          </Form.Field>

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <Grid gridRowGap="medium">
                <ArtworkButton campaignQuoteItemPayload={campaignQuoteItemPayload} />

                {isQuantityEditable && !package_id && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faPencil} />}
                    onClick={() => editQuantityAndOptions({ validateFn })}
                    size="medium"
                  >
                    Edit Qty & Options
                  </Button>
                )}

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={hidden ? faEye : faEyeSlash} />}
                  onClick={() => setEntityState({ hidden: !hidden })}
                  size="medium"
                >
                  {hidden ? 'Show' : 'Hide'} Item on Quote
                </Button>

                {!!deep_link_url && (
                  <Button
                    // @ts-expect-error TS2322
                    as="a"
                    buttonStyle="secondaryUtility"
                    href={deep_link_url}
                    icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                    rel="noopener noreferrer"
                    size="medium"
                    target="_blank"
                  >
                    {deep_link?.link_text || 'Manage Item'}
                  </Button>
                )}

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCopy} />}
                  onClick={() => duplicateCampaignQuoteItem({
                    callbacks: {
                      // @ts-expect-error TS2322
                      action: duplicateFn,
                      afterAction: closeModal,
                      // @ts-expect-error TS2322
                      buildActionParams: () => [requestOptions],
                    },
                    entityParams: {},
                    toastText: 'Quote Item Duplicated',
                  })}
                  size="medium"
                >
                  Duplicate Item
                </Button>

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={() => confirmDelete({
                    callbacks: {
                      afterAction: closeModal,
                      deleteFn,
                    },
                    strings: {
                      entityName: 'Quote Item',
                    },
                  })}
                  size="medium"
                >
                  Delete Item
                </Button>

              </Grid>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuoteItem(createOrUpdateCampaignQuoteItemPayload)
              : () => updateCampaignQuoteItem(createOrUpdateCampaignQuoteItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Quote Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
